import React from "react";
import { useUser } from "src/shared/context/UserContext";
import { COMPANY_NAME } from "src/shared/utils/brand";
import noImage from "src/assets/Images/imetanic-logo.png";

interface DailyReportPrinterProps {
  totalSales: number;
  totalAmount: number;
  totalOrders: number;
  openingBalance: number;
  totalCashSales: number;
  totalCardSales: number;
  totalTips: number;
  totalDiscounts: number;
  expected: number;
  counted: string;
  difference: number;
  businessDay: string;
  numberOfPendingOrders: number;
  shift: string;
  type: string;
  receivableAmount: number;
  totalCashTips: number;
  topSales: {
    productName: string;
    numberOfSales: number;
  }[];
}

const DailyReportPrinter: React.FC<DailyReportPrinterProps> = ({
  totalSales = 0,
  totalOrders = 0,
  openingBalance = 0,
  totalCashSales = 0,
  totalCardSales = 0,
  totalTips = 0,
  totalDiscounts = 0,
  expected = 0,
  counted = 0,
  difference = 0,
  businessDay = "",
  shift = "",
  topSales = [],
  totalAmount = 0,
  type = "",
  numberOfPendingOrders = 0,
  receivableAmount = 0,
  totalCashTips = 0,
}) => {
  const { user, userConfigration } = useUser();

  const now = new Date();

  const formattedDate = businessDay ? new Date(businessDay).toLocaleDateString("en-GB") : "-";

  const formattedTime = now.toLocaleTimeString("en-GB", { hour12: false });

  const formatCurrency = (amount: number) =>
    Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="w-full max-w-[280px] font-mono text-[12px] p-1 m-2 text-black">
      <div className="text-center">
        <img src={user?.companyLogo || noImage} onError={(e: any) => (e.target.src = noImage)} className="w-[60px] h-[60px] object-contain mx-auto block" alt="logo" />
        <div className="font-bold text-[14px] mt-1">{user?.orgName || "Business Name"}</div>
        <div className="font-bold text-[14px] mt-1">{user?.orgArabicName || "Business Name"}</div>
        {user?.addresses.addressLine1 && <div>{user.addresses.addressLine1}</div>}
        {user?.phones && <div>MOB - {user.phones[0].phone}</div>}
      </div>

      <div className="border-t border-dashed border-black my-1" />
      <div className="text-center font-bold">END OF DAY REPORT</div>
      <div className="border-t border-dashed border-black my-2" />

      <table className="w-full text-xs">
        <tbody>
          <tr>
            <td>Type : {type || "N/A"}</td>
            <td className="text-right">Date : {formattedDate}</td>
          </tr>
          <tr>
            <td>Cashier : {user?.userName || "N/A"}</td>
            <td className="text-right">Time : {formattedTime}</td>
          </tr>
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />

      <div className="font-bold">SALES SUMMARY</div>
      <table className="w-full text-xs">
        <tbody>
          <tr>
            <td>Total Sales</td>
            <td className="text-right">{formatCurrency(totalSales)}</td>
          </tr>
          <tr>
            <td>Total Orders</td>
            <td className="text-right">{totalOrders}</td>
          </tr>
          <tr>
            <td>Pending Orders</td>
            <td className="text-right">{numberOfPendingOrders}</td>
          </tr>
          <tr>
            <td>Receivable Amount:</td>
            <td className="text-right">{formatCurrency(receivableAmount)}</td>
          </tr>
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />

      <div className="font-bold">CASH COUNT</div>
      <table className="w-full text-xs">
        <tbody>
          <tr>
            <td>Opening Balance</td>
            <td className="text-right">{formatCurrency(openingBalance)}</td>
          </tr>
          <tr>
            <td>Total Cash Sales</td>
            <td className="text-right">{formatCurrency(totalCashSales)}</td>
          </tr>
          <tr>
            <td>Total Cash Tips</td>
            <td className="text-right">{formatCurrency(totalCashTips)}</td>
          </tr>
          <tr>
            <td>Expected</td>
            <td className="text-right">{formatCurrency(expected)}</td>
          </tr>
          <tr>
            <td>Counted</td>
            <td className="text-right">{formatCurrency(Number(counted))}</td>
          </tr>
          <tr>
            <td>Difference</td>
            <td className="text-right font-bold">{formatCurrency(difference)}</td>
          </tr>
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />

      <div className="font-bold">PAYMENT BREAKDOWN</div>
      <table className="w-full text-xs">
        <tbody>
          <tr>
            <td>Total Card Amount</td>
            <td className="text-right">{formatCurrency(totalCardSales)}</td>
          </tr>
          <tr>
            <td>Total Cash Amount</td>
            <td className="text-right">{formatCurrency(totalCashSales)}</td>
          </tr>
          <tr>
            <td>Total Tips</td>
            <td className="text-right">{formatCurrency(totalTips)}</td>
          </tr>
          <tr>
            <td>Total Discounts</td>
            <td className="text-right">-{formatCurrency(totalDiscounts)}</td>
          </tr>
        </tbody>
      </table>

      {/* Full dashed line below Total Discounts and above Total Amount */}
      <div className="border-t border-dashed border-black my-2 w-full" />

      <table className="w-full text-xs">
        <tbody>
          <tr>
            <td className="font-bold">Total Amount</td>
            <td className="text-right font-bold">{formatCurrency(totalAmount)}</td>
          </tr>
        </tbody>
      </table>
      <div className="border-t border-dashed border-black my-2" />
      <div className="font-bold">TOP SELLING PRODUCTS (5)</div>

      <table className="w-full text-xs mt-1">
        <tbody>
          {topSales.length === 0 ? (
            <tr>
              <td colSpan={2} className="text-center">
                No top products
              </td>
            </tr>
          ) : (
            topSales.map((item, index) => (
              <tr key={index}>
                <td className="truncate">{item.productName}</td>
                <td className="text-right">{item.numberOfSales}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="border-t border-dashed border-black my-2" />
      <div className="text-center font-bold">STATUS : DAY CLOSED</div>
      {/* Terms & Conditions - English */}
      {userConfigration?.enableTermsAndConditions && userConfigration?.termsAndConditionsEnglish && (
        <div className="mt-2 border-t border-gray-200 pt-2">
          <strong>Terms and Conditions:</strong>
          <p className="text-xs mt-1">{userConfigration.termsAndConditionsEnglish}</p>
        </div>
      )}

      {/* Terms & Conditions - Arabic */}
      {userConfigration?.enableTermsAndConditions && userConfigration?.termsAndConditionsArabic && (
        <div className="mt-2 text-[14px]">
          <p>{userConfigration.termsAndConditionsArabic}</p>
        </div>
      )}

      {/* Contact QR Code */}
      {userConfigration?.contactQrCode && (
        <div className="text-center mt-3">
          <strong>Scan QR code to contact us on WhatsApp:</strong>
          <img src={userConfigration.contactQrCode} alt="Contact QR" className="w-[160px] h-[160px] mx-auto mt-2 object-contain" />
        </div>
      )}

      {/* Location QR Code */}
      {userConfigration?.locationQrCode && (
        <div className="text-center mt-3">
          <strong>Scan QR code for our location:</strong>
          <img src={userConfigration.locationQrCode} alt="Location QR" className="w-[160px] h-[160px] mx-auto mt-2 object-contain" />
        </div>
      )}

      <div className="border-t border-dashed border-black my-2" />
      <div className="text-center text-xs">Thank you for your purchase!</div>
      <div className="text-center text-xs">Powered by {COMPANY_NAME}</div>
    </div>
  );
};

export default DailyReportPrinter;
